import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import Button from '@material-ui/core/Button';
import { CircularBar } from 'components/shared/CircularBar';
import { CustomDropdownReadOnly } from 'components/shared/DropDown/CustomDropdown';
import ErrorComponent from 'components/shared/ErrorComponent';
import { Loader } from 'components/shared/Loader';
import { Paginator } from 'components/shared/Paginator';
import { ProgressBar } from 'components/shared/ProgressBar';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import OrderTypeFilter from 'components/shared/Filter/OrderTypeFilter';
import OrderToBeVerifiedFilter from 'components/shared/Filter/OrderTypeVerified';

import {
  FILTER_CAMPAIGN_TO_VERIFY_LIST,
  FILTER_CAMPAIGN_TYPE_LIST,
  PIXLINK_PUBLISH_STATUS,
  PIXLINK_REDAC_STATUS,
  PIXLINK_STATUS,
  PIXLINK_ADVANCEMENT_STATUS,
  CAMPAIGN_STATUS_SOURCING_LABELS,
} from 'utils/Constants';

import { useHistoryState } from 'utils/hooks/useHistoryState';

import { formatDate } from 'utils/DateUtils';

import { filterOrders } from 'helpers/FilterOrders';

import axiosPixLink from 'config/axiosPixLink';
import './order.css';
import { UserSessionSelector } from 'store/UserSession';
import { calculateMinimumLinks } from 'utils/netlinking/linksNumber';

const pageNbOptions = [10, 15, 20];

export default function AdminOrdersByWebsite() {
  /*let { inProgressOrders, isLoading, isError } = useSelector(
adminOrderListSelector
);*/
  const { id } = useParams();

  let { user } = useSelector(UserSessionSelector);
  const { t } = useTranslation();
  const [openGroups, setOpenGroups] = useState({});
  const [groupBudgets, setGroupBudgets] = useState({});

  const toggleGroup = (devisId) => {
    setOpenGroups((prev) => {
      const isCurrentlyOpen = prev[devisId] !== false;
      return { ...prev, [devisId]: !isCurrentlyOpen };
    });
  };
  const IN_PROGRESS_ADVANCEMENT_STATUSES = [
    PIXLINK_ADVANCEMENT_STATUS.A_COMMANDER,
    PIXLINK_ADVANCEMENT_STATUS.EN_COURS_DE_REDACTION,
    PIXLINK_ADVANCEMENT_STATUS.A_VALIDER_CLIENT,
    PIXLINK_ADVANCEMENT_STATUS.VALIDE_CLIENT_A_PUBLIER,
    PIXLINK_ADVANCEMENT_STATUS.EN_COURS_DE_MODIFICATION,
    PIXLINK_ADVANCEMENT_STATUS.EN_COURS_DE_PUBLICATION,
  ];
  const columns = [
    { label: t('Titre de la campagne'), field: 'title', sortable: true },
    { label: t('Sourcing'), field: 'sourcing', sortable: false },
    { label: t('Type'), field: 'type', sortable: false },
    { label: t('To Be Verified'), field: 'toBeVerified', sortable: false },
    { label: t('Deadline'), field: 'deadline', sortable: true, sort: 'asc' },
    { label: t('Consultants'), field: 'writers', sortable: false },
    {
      label: t('Liens en cours'),
      field: 'proposedLinksAmount',
      sortable: true,
      sort: 'asc',
    },
    {
      label: t('Liens en ligne'),
      field: 'commandedLinksAmount',
      sortable: true,
      sort: 'asc',
    },
    { label: t('Budget restant'), field: 'budgetRestant', sortable: false },
    {
      label: t('Temps restant'),
      field: 'finishedAt',
      sortable: true,
      sort: 'asc',
    },
  ];

  const fieldWidths = {
    title: '26%',
    sourcing: '14%',
    deadline: '10%',
    type: '10%',
    toBeVerified: '10%',
  };

  const dispatch = useDispatch();
  const history = useHistory();
  const [inProgressOrders, setInProgressOrders] = useState([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage, setOrdersPerPage] = useState(10);
  const [currentOrders, setCurrentOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [websiteTitle, setWebsiteTitle] = useState('');
  const [selectedVerified, setSelectedVerified] = useState(
    FILTER_CAMPAIGN_TO_VERIFY_LIST[0]
  );
  const [selectedType, setSelectedType] = useState(
    FILTER_CAMPAIGN_TYPE_LIST[0]
  );

  const [pageNumbers, setPageNumbers] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorElToBeVerified, setAnchorElToBeVerified] = useState(null);

  const [titleSearch, setTitleSearch] = useHistoryState('titleSearch', '');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  const groupedOrders = useMemo(() => {
    return currentOrders.reduce((acc, order) => {
      const key = order.idDevis;
      if (!acc[key]) acc[key] = [];
      acc[key].push(order);
      return acc;
    }, {});
  }, [currentOrders]);

  useEffect(() => {
    document.title = t('PixLink - campagnes');
    window.dispatchEvent(new Event('locationchange'));
    setIsLoading(true);
    axiosPixLink
      .post('/campaign/findClientsCampaignsByWebsite', id)
      .then((res) => {
        const campaigns = res.data.campaigns;
        const resteAConsommerByTask = res.data.resteAConsommerByTask || {};
        setInProgressOrders(campaigns);
        setWebsiteTitle(res.data.website);
        setIsLoading(false);
        handleSortingChange('deadline');

        const budgets = {};
        Object.keys(resteAConsommerByTask).forEach((key) => {
          budgets[key] = resteAConsommerByTask[key];
        });
        setGroupBudgets(budgets);
      });
  }, []);

  useEffect(() => {
    if (!inProgressOrders) return;

    const filtered = filterOrders(
      inProgressOrders,
      selectedVerified,
      selectedType,
      titleSearch
    );
    if (titleSearch && titleSearch.length > 0) {
      setCurrentPage(1);
    }
    setFilteredOrders(filtered);
  }, [selectedVerified, selectedType, titleSearch, inProgressOrders]);

  useEffect(() => {
    const indexOfLastOrder = currentPage * ordersPerPage;
    const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
    setCurrentOrders(filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder));
    setPageNumbers(
      [...Array(Math.ceil(filteredOrders.length / ordersPerPage)).keys()].map(
        (i) => i + 1
      )
    );
  }, [currentPage, filteredOrders, ordersPerPage]);

  useEffect(() => {
    if (
      pageNumbers.length > 0 &&
      pageNumbers[pageNumbers.length - 1] < currentPage
    )
      setCurrentPage(pageNumbers[pageNumbers.length - 1]);
  }, [pageNumbers]);

  const handleClick = (index, event) => {
    setAnchorEl({ [index]: event.currentTarget });
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleClickToBeVerified = (index, event) => {
    setAnchorElToBeVerified({ [index]: event.currentTarget });
  };

  const handleCloseToBeVerified = () => {
    setAnchorElToBeVerified(null);
  };
  const getSourcingLabel = (campaignStatus) => {
    if (!campaignStatus) return '-';
    return CAMPAIGN_STATUS_SOURCING_LABELS[campaignStatus] || campaignStatus;
  };

  const handleSortingChange = (field) => {
    const direction =
      field === sortField && sortOrder === 'asc' ? 'desc' : 'asc';

    const sortedOrders = [...filteredOrders].sort((a, b) => {
      if (field === 'deadline' || field === 'finishedAt') {
        const dateA = new Date(a['deadLine']).getTime();
        const dateB = new Date(b['deadLine']).getTime();

        return direction === 'asc' ? dateA - dateB : dateB - dateA;
      } else if (field === 'proposedLinksAmount') {
        return direction === 'asc'
          ? a['netlinkingSelecteds'].filter((item) =>
            IN_PROGRESS_ADVANCEMENT_STATUSES.includes(item.etatAvancement)
          )?.length /
          a['linksNumbers'] -
          b['netlinkingSelecteds'].filter((item) =>
            IN_PROGRESS_ADVANCEMENT_STATUSES.includes(item.etatAvancement)
          )?.length /
          b['linksNumbers']
          : b['netlinkingSelecteds'].filter((item) =>
            IN_PROGRESS_ADVANCEMENT_STATUSES.includes(item.etatAvancement)
          )?.length /
          b['linksNumbers'] -
          a['netlinkingSelecteds'].filter((item) =>
            IN_PROGRESS_ADVANCEMENT_STATUSES.includes(item.etatAvancement)
          )?.length /
          a['linksNumbers'];
      } else if (field === 'commandedLinksAmount') {
        return direction === 'asc'
          ? a['netlinkingSelecteds'].filter(
            (item) => item.etatAvancement === PIXLINK_ADVANCEMENT_STATUS.PUBLIE
          )?.length /
          a['linksNumbers'] -
          b['netlinkingSelecteds'].filter(
            (item) => item.etatAvancement === PIXLINK_ADVANCEMENT_STATUS.PUBLIE
          )?.length /
          b['linksNumbers']
          : b['netlinkingSelecteds'].filter(
            (item) => item.etatAvancement === PIXLINK_ADVANCEMENT_STATUS.PUBLIE
          )?.length /
          b['linksNumbers'] -
          a['netlinkingSelecteds'].filter(
            (item) => item.etatAvancement === PIXLINK_ADVANCEMENT_STATUS.PUBLIE
          )?.length /
          a['linksNumbers'];
      } else if (field === 'title') {
        const aTitle = `${a.numeroDevis || a.idDevis} - ${a.account} - ${a.taskName} - ${a.linksNumbers}`;
        const bTitle = `${b.numeroDevis || b.idDevis} - ${b.account} - ${b.taskName} - ${b.linksNumbers}`;
        return direction === 'asc'
          ? aTitle.localeCompare(bTitle)
          : bTitle.localeCompare(aTitle);
      }

      return 0;
    });
    setSortField(field);
    setSortOrder(direction);
    setFilteredOrders(sortedOrders);
  };

  return (
    <div className="content-center">
      <div className="box-info-header">
        <span onClick={history.goBack} className="bt-circle me-3" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ wordBreak: 'break-word', whiteSpace: 'normal', margin: 0 }}>
            {websiteTitle}
          </h1>
        </div>
        <div className="b-child-2">
          <div className="d-flex justify-content-end">
            <div className="box-input-text pe-0">
              <div className="input-group input-cs w-100 pe-0">
                <div className="input-group-text">
                  <i className="bi bi-search" />
                </div>
                <input
                  type="text"
                  className="form-control ps-0"
                  value={titleSearch}
                  onChange={(event) => setTitleSearch(event.target.value)}
                  placeholder={t('Rechercher')}
                />
              </div>
            </div>
            <div className="d-flex align-items-center">
              <span className="me-2">{t('Afficher les résultats')}</span>

              <div className="dropdown" style={{ width: '67px' }}>
                <button
                  className="btn dropdown-toggle dropdown-custom w-100"
                  type="button"
                  id="dropdownMenuButton1"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  {ordersPerPage}
                  <span>
                    <i className="bi bi-chevron-down" />
                  </span>
                </button>
                <ul
                  className="dropdown-menu"
                  aria-labelledby="dropdownMenuButton1"
                >
                  {pageNbOptions
                    .filter((nb) => nb !== ordersPerPage)
                    .map((item, index) => (
                      <li key={index} onClick={() => setOrdersPerPage(item)}>
                        <a className="dropdown-item">{item}</a>
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isLoading ? (
        <Loader />
      ) : isError ? (
        <ErrorComponent />
      ) : (
        <div className="box-wrapper">
          <div className="table-responsive">
            <div>
              <table className="table-lists table-commandes">
                <thead>
                  <tr>
                    {columns.map(({ label, field, sortable }) => (
                      <th
                        style={{
                          cursor: sortable ? 'pointer' : 'default',
                          width: fieldWidths[field] || '',
                        }}
                        onClick={
                          sortable ? () => handleSortingChange(field) : null
                        }
                        key={field}
                      >
                        {label}
                        {!sortable ? null : field === sortField &&
                          sortOrder === 'asc' ? (
                          <span>
                            <i
                              style={{ marginLeft: '3px' }}
                              className="bi bi-chevron-up"
                            />
                          </span>
                        ) : (
                          <span>
                            <i
                              style={{ marginLeft: '3px' }}
                              className="bi bi-chevron-down"
                            />{' '}
                          </span>
                        )}{' '}
                        {field === 'type' ? (
                          <OrderTypeFilter
                            orderTypeList={FILTER_CAMPAIGN_TYPE_LIST}
                            selectedType={selectedType}
                            setSelectedType={setSelectedType}
                            handleClick={handleClick}
                            handleClose={handleClose}
                            anchorEl={anchorEl}
                          />
                        ) : null}
                        {field === 'toBeVerified' ? (
                          <OrderToBeVerifiedFilter
                            orderVerifiedList={FILTER_CAMPAIGN_TO_VERIFY_LIST}
                            selectedVerified={selectedVerified}
                            setSelectedVerified={setSelectedVerified}
                            handleClick={handleClickToBeVerified}
                            handleClose={handleCloseToBeVerified}
                            anchorEl={anchorElToBeVerified}
                          />
                        ) : null}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(groupedOrders).map(([devisId, orders]) => {
                    const isOpen = openGroups[devisId] !== false;

                    const sumInProgress = orders.reduce(
                      (s, o) =>
                        s +
                        (o.netlinkingSelecteds?.filter((item) =>
                          IN_PROGRESS_ADVANCEMENT_STATUSES.includes(item.etatAvancement)
                        ).length || 0),
                      0
                    );

                    const sumPublished = orders.reduce(
                      (s, o) =>
                        s +
                        (o.netlinkingSelecteds?.filter(
                          (item) => item.etatAvancement === PIXLINK_ADVANCEMENT_STATUS.PUBLIE
                        ).length || 0),
                      0
                    );

                    const groupBudgetTotal = orders.reduce((sum, o) => {
                      const val = groupBudgets[`${o.idDevis}|${o.task}`];
                      return sum + (typeof val === 'number' ? val : 0);
                    }, 0);
                    const groupBudgetDisplay = `${groupBudgetTotal.toLocaleString('fr-FR')} €`;

                    return (
                      <>
                        <tr
                          key={`group-${devisId}`}
                          style={{ cursor: 'pointer', fontWeight: 'bold' }}
                          onClick={() => toggleGroup(devisId)}
                        >
                          <td style={{ backgroundColor: '#d6d6d6' }}>
                            <span style={{ marginRight: 8 }}>{isOpen ? '▾' : '▸'}</span>
                            {orders[0]?.numeroDevis} — {orders[0]?.account}
                            <span style={{ fontWeight: 'normal', marginLeft: 8, fontSize: '0.85em', color: '#666' }}>
                              ({orders.length} campagne{orders.length > 1 ? 's' : ''})
                            </span>
                          </td>
                          <td style={{ backgroundColor: '#d6d6d6' }} />
                          <td style={{ backgroundColor: '#d6d6d6' }} />
                          <td style={{ backgroundColor: '#d6d6d6' }} />
                          <td style={{ backgroundColor: '#d6d6d6' }} />
                          <td style={{ backgroundColor: '#d6d6d6' }} />
                          <td style={{ backgroundColor: '#d6d6d6' }}>{sumInProgress}</td>
                          <td style={{ backgroundColor: '#d6d6d6' }}>{sumPublished}</td>
                          <td style={{ backgroundColor: '#d6d6d6' }}>{groupBudgetDisplay}</td>
                          <td style={{ backgroundColor: '#d6d6d6' }} />
                        </tr>
                        {/* Lignes campagnes (détail) */}
                        {isOpen && orders.map((order, index) => {
                          const orderBudget = groupBudgets[`${order.idDevis}|${order.task}`];
                          const orderBudgetDisplay =
                            orderBudget === undefined
                              ? '—'
                              : `${orderBudget.toLocaleString('fr-FR')} €`;

                          return (
                            <tr key={index}>
                              <td
                                style={{ cursor: 'pointer', paddingLeft: '2rem' }}
                                onClick={() => history.push('/Admin/DetailsCampagne/' + order.id)}
                              >
                                <span>     {`${order.numeroDevis || order.idDevis} - ${order.account} - ${order.taskName}`}
                                  {order.intitule && <span> - {order.intitule}</span>}
                                </span>
                              </td>
                              <td>{getSourcingLabel(order?.campaignStatus)}</td>
                              <td>{t(order?.type ? order?.type === 'ultraPremium' ? 'ultra premium' : order?.type : 'standard')}</td>
                              <td>{t(order.toBeVerified ? 'à verifier' : '-')}</td>
                              <td><span>{order?.deadLine ? formatDate(order?.deadLine) : '-'}</span></td>
                              <td>
                                <div>
                                  {order?.consultants.length === 0 ? (
                                    <Button disabled>{t('0 Consultants')}</Button>
                                  ) : (
                                    <>
                                      <Button
                                        aria-controls="customized-menu"
                                        aria-haspopup="true"
                                        onClick={(e) => handleClick(index, e)}
                                      >
                                        <img src="/Images/icon-users-down.png" alt="icon-users-down" />
                                      </Button>
                                      <CustomDropdownReadOnly
                                        handleClose={handleClose}
                                        anchorEl={anchorEl && anchorEl[index]}
                                        list={order?.consultants}
                                        getOptionLabel={(option) => option.email}
                                      />
                                    </>
                                  )}
                                </div>
                              </td>
                              <td>
                                {order?.netlinkingSelecteds.filter((item) =>
                                  IN_PROGRESS_ADVANCEMENT_STATUSES.includes(item.etatAvancement)
                                )?.length}
                              </td>
                              <td>
                                {order?.netlinkingSelecteds.filter(
                                  (item) => item.etatAvancement === PIXLINK_ADVANCEMENT_STATUS.PUBLIE
                                )?.length}
                              </td>
                              <td>{orderBudgetDisplay}</td>
                              <td>
                                <CircularBar finishedAt={order?.deadLine} orderId={order?.id} />
                              </td>
                            </tr>
                          );
                        })}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      <Paginator
        isLoading={isLoading}
        listLength={filteredOrders.length}
        currentPage={currentPage}
        pageNumbers={pageNumbers}
        nbElementsPerPage={ordersPerPage}
        goToPage={(page) => setCurrentPage(page)}
      />
    </div>
  );
}